/**
 * Guide Service
 *
 * API service for fetching CoPilot Guide configurations.
 * Proxies to brain-service via platform-api.
 * Includes runtime schema validation (Fix F).
 */

import { TonnieClient } from '../client/base';
import type { CoPilotGuideConfig } from '@tonnie/copilot';

// Re-export for convenience
export type { CoPilotGuideConfig };

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Flow IDs supported by the guide system
 */
export type GuideFlowId = 'checkup' | 'teleportal' | 'qultureup' | 'journey_builder';

/**
 * API response wrapper from brain-service via platform-api
 */
export interface GuideConfigResponse {
  success: boolean;
  data: {
    configType: string;
    version: string;
    source?: string;
    content: CoPilotGuideConfig;
  };
}

// ═══════════════════════════════════════════════════════════════════════
// DEBUG HELPER
// ═══════════════════════════════════════════════════════════════════════

const isDevMode = typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'development';

const svcDbg = (...args: unknown[]) => {
  if (isDevMode) {
    console.log('[GuideService]', ...args);
  }
};

// ═══════════════════════════════════════════════════════════════════════
// VALIDATION (Fix F)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Runtime validation for CoPilotGuideConfig.
 * Validates the essential structure: config.content.tasks must be a non-empty
 * array where each task has an id and a chats array.
 */
function validateGuideConfig(config: unknown, flowId: string): config is CoPilotGuideConfig {
  if (!config || typeof config !== 'object') {
    svcDbg(`Config for "${flowId}" is not an object`);
    return false;
  }

  const cfg = config as Record<string, unknown>;
  const contentObj = cfg['content'] as Record<string, unknown> | undefined;

  if (!contentObj || typeof contentObj !== 'object') {
    svcDbg(`Config for "${flowId}" missing content object`);
    return false;
  }

  const tasks = contentObj['tasks'] as unknown[];

  if (!tasks || !Array.isArray(tasks) || tasks.length === 0) {
    svcDbg(`Config for "${flowId}" has no valid content.tasks array`);
    return false;
  }

  // Validate each task has id and chats
  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i] as Record<string, unknown>;
    if (!task?.['id'] || typeof task['id'] !== 'string') {
      svcDbg(`Config for "${flowId}": task[${i}] missing id`);
      return false;
    }
    if (!Array.isArray(task['chats'])) {
      svcDbg(`Config for "${flowId}": task "${String(task['id'])}" missing chats array`);
      return false;
    }
  }

  return true;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE FACTORY
// ═══════════════════════════════════════════════════════════════════════

export function createGuideService(client: TonnieClient) {
  return {
    /**
     * Fetch the guide configuration for a given flow.
     * GET /api/copilot/guide/:flowId
     *
     * The API returns: { success, data: { configType, version, source, content } }
     * where `content` is the inner content from the DB (e.g. { tasks: [...] }).
     *
     * The CoPilotGuideEngine expects a full CoPilotGuideConfig shape:
     * { id, kind, version, active, scope, content: { tasks: [...] } }
     *
     * This method reconstructs the full shape from the API response.
     */
    async getGuideConfig(flowId: GuideFlowId | string): Promise<CoPilotGuideConfig> {
      svcDbg('getGuideConfig() — flowId:', flowId);
      // The platform-api response shape is `CoPilotGuideConfig` directly
      // (radar 2026-04-28 — H-1 follow-up): the controller forwards the
      // result of `guideConfigSchema.safeParse(...)`, which strips the
      // brain envelope and returns `{ id, kind, scope, active, content,
      // version }`. Older deployments returned `{ success, data: {
      // configType, version, content } }`; we tolerate both for
      // forward/backward compat during a deploy.
      const response = await client.get<unknown>(`/api/copilot/guide/${flowId}`);

      let guideConfig: CoPilotGuideConfig | null = null;

      if (response && typeof response === 'object') {
        const r = response as Record<string, unknown>;

        // New shape — parsed config returned directly.
        if (typeof r['id'] === 'string' && r['content']) {
          guideConfig = response as CoPilotGuideConfig;
        }

        // Legacy envelope `{ success, data: { content: <config> } }`.
        if (!guideConfig && r['data'] && typeof r['data'] === 'object') {
          const data = r['data'] as Record<string, unknown>;
          if (data['content'] && typeof data['content'] === 'object') {
            const inner = data['content'] as Record<string, unknown>;
            if (typeof inner['id'] === 'string' && inner['content']) {
              guideConfig = inner as unknown as CoPilotGuideConfig;
            } else if (Array.isArray(inner['tasks'])) {
              guideConfig = {
                id: `${flowId}.${(data['version'] as string) || 'v1'}`,
                kind: 'assistant',
                version: (data['version'] as string) || 'v1',
                active: true,
                scope: { tenant: 'default', market: 'global', language: 'en-US' },
                content: inner as unknown as CoPilotGuideConfig['content'],
              } as CoPilotGuideConfig;
            }
          }
        }
      }

      if (!guideConfig) {
        throw new Error(
          `Invalid guide config received for flow "${flowId}". No content in response.`
        );
      }

      if (!validateGuideConfig(guideConfig, flowId)) {
        throw new Error(`Invalid guide config received for flow "${flowId}". Validation failed.`);
      }

      svcDbg('getGuideConfig() — validated config for flow:', flowId);
      return guideConfig;
    },
  };
}
